import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NotificationBell, buildBellLabels, type NotificationBellItem } from '@munifocus/ui';
import { useInbox, useMarkAllRead, useMarkRead } from '../lib/notifications-api';
import { inPortalPath } from '../lib/teamwork-api';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * The topbar bell, wired to gov-api.
 *
 * Thin on purpose: the presentation is `@munifocus/ui`'s `NotificationBell`,
 * shared with the Admin Portal, and this component only supplies data, copy and
 * navigation. Clicking a row marks it read *and* follows its link, because a
 * notification the officer has acted on should not still be shouting at them.
 */
export function NotificationBellMenu({ onDark = false }: { onDark?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const inbox = useInbox();
  const markAllRead = useMarkAllRead();
  const markRead = useMarkRead();

  const language = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const unread = inbox.data?.unread ?? 0;

  const items: NotificationBellItem[] = (inbox.data?.items ?? []).map((item) => ({
    id: item.id,
    title: item.title[language],
    body: item.body[language],
    timestamp: frenchFirstOfMonth(
      new Intl.DateTimeFormat(language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(item.createdAt)),
    ),
    read: item.read,
    linkUrl: item.linkUrl,
  }));

  return (
    <NotificationBell
      items={items}
      unread={unread}
      loading={inbox.isLoading}
      onDark={onDark}
      onOpen={() => inbox.refetch()}
      onMarkAllRead={() => markAllRead.mutate()}
      onSelect={(item) => {
        if (!item.read) markRead.mutate(item.id);
        // Only in-portal paths are followed. Rows store the absolute portal
        // URL the email carries; `inPortalPath` keeps the path of one this
        // portal serves and ignores anything else (an emailed form link).
        const path = inPortalPath(item.linkUrl);
        if (path) navigate(path);
      }}
      footerAction={{ label: t('gov.teamwork.inbox.open'), onClick: () => navigate('/inbox') }}
      labels={buildBellLabels(t, unread)}
    />
  );
}
